import {englishLabel, FormatLabel, LabelDescriptor} from './projectStatusLabels';

/**
 * The project budget-type vocabulary, beside `projectStageLabels` and for the same reason: the wire
 * tokens (`TIME_AND_MATERIALS`, `FIXED_PRICE_V2`) are not what a reader is shown.
 *
 * Two vocabularies, both DECIDED on TNSR-638. A row - the cell, the group heading, the truncation
 * tooltip and the CSV - reads singular, because a single project IS a Retainer. The condition picker
 * mirrors the standard report's "Filter Projects by -> Budget Type" list
 * (`Util.getProjectTypeFilterOptions`) verbatim, plural Retainers and capital-B Non-Billable, because
 * there the reader is picking a category and the product should not fork a third spelling of it.
 * Both reuse existing keys, already translated into every locale we ship, except the row's
 * `Non-billable`, which no existing key spells.
 *
 * Both fixed-price enums read `Fixed Price`: `FIXED_PRICE_V2` only changes how a project's progress is
 * derived, which is not a budget type a customer chose. Merging them is safe only because
 * report-service widens a filter on either token to both (`ProjectStatusFilterPartition`) - without
 * that, the merged picker option would silently lose whichever enum it did not store.
 *
 * `Map`s rather than object literals because the key is a raw wire string; see `projectStageLabels`.
 */

const FIXED_PRICE: LabelDescriptor = {id: 'common.fixed_price', defaultMessage: 'Fixed Price'};

const ROW_MESSAGE = new Map<string, LabelDescriptor>([
	['FIXED_PRICE', FIXED_PRICE],
	['FIXED_PRICE_V2', FIXED_PRICE],
	['TIME_AND_MATERIALS', {id: 'common.time_material', defaultMessage: 'Time & Materials'}],
	['RETAINER', {id: 'common.retainer', defaultMessage: 'Retainer'}],
	['NON_BILLABLE', {id: 'ai_report_builder.budget_type.non_billable', defaultMessage: 'Non-billable'}],
]);

const FILTER_MESSAGE = new Map<string, LabelDescriptor>([
	['FIXED_PRICE', FIXED_PRICE],
	['FIXED_PRICE_V2', FIXED_PRICE],
	['TIME_AND_MATERIALS', {id: 'new_project_modal.budget_type_time_materials', defaultMessage: 'Time & Materials'}],
	['RETAINER', {id: 'common.retainers', defaultMessage: 'Retainers'}],
	['NON_BILLABLE', {id: 'common.non-billable', defaultMessage: 'Non-Billable'}],
]);

/** Stored values the picker shows as another value's option. `FIXED_PRICE_V2` is the one kept
 * because it is the live value; `FIXED_PRICE` survives only on long-idle projects. */
export const BUDGET_TYPE_ALIASES: ReadonlyMap<string, string> = new Map([['FIXED_PRICE', 'FIXED_PRICE_V2']]);

/** The picker's option order: the standard report's. */
export const BUDGET_TYPE_FILTER_ORDER: readonly string[] = ['TIME_AND_MATERIALS', 'RETAINER', 'FIXED_PRICE_V2', 'NON_BILLABLE'];

/**
 * What a row shows for a raw `projectStatus.budgetType` value.
 *
 * An unrecognised token is echoed rather than hidden, as `stageLabelFor` does: it still names a real
 * budget type, and the token tells the reader more than a blank. A missing budget type answers with
 * an empty string - no label, rather than a blank one - and the row mapper shows it as `N/A`.
 */
export function budgetTypeLabelFor(wireValue: unknown, formatLabel: FormatLabel = englishLabel): string {
	if (typeof wireValue !== 'string' || wireValue === '') {
		return '';
	}
	const message = ROW_MESSAGE.get(wireValue);
	return message ? formatLabel(message) : wireValue;
}

/** What the condition picker shows for a stored budget type. An unrecognised token is echoed. */
export function budgetTypeFilterLabelFor(wireValue: string, formatLabel: FormatLabel = englishLabel): string {
	const message = FILTER_MESSAGE.get(wireValue);
	return message ? formatLabel(message) : wireValue;
}

/** Every stored budget type with its row label: the CSV export's lookup table. */
export function projectBudgetTypeRowCandidates(formatLabel: FormatLabel = englishLabel): {id: string; label: string}[] {
	return [...ROW_MESSAGE.keys()].map(value => ({id: value, label: budgetTypeLabelFor(value, formatLabel)}));
}
